import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Breadcrumbs from "@/components/Breadcrumbs";
import PawDivider from "@/components/PawDivider";
import { Icon } from "@/components/iconMap";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { EstimateCTASection, SectionHeading } from "@/components/Sections";
import { EstimateButton } from "@/components/CTAButtons";
import { pricing, priceIncludes, business } from "@/data/site";
import { PawPrint, Info } from "lucide-react";

export default function Pricing() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Junk Removal",
    provider: { "@type": "LocalBusiness", name: business.name },
    offers: pricing.map((p) => ({
      "@type": "Offer",
      name: p.size,
      price: p.price,
      priceCurrency: "USD",
      description: `${p.size}, approx. ${p.volume}`,
    })),
  };

  return (
    <>
      <Seo
        title="Junk Removal Pricing | Starting at $99 | Benji's Hauling"
        description="See straightforward junk removal pricing from Benji's Hauling & Junk Removal. Single-item pickups start at $99 with loading, hauling, cleanup, and free estimates included."
        path="/pricing"
        jsonLd={jsonLd}
      />
      <Breadcrumbs items={[{ label: "Pricing" }]} />
      <PageHero
        eyebrow="Pricing"
        title="Simple, Straightforward Junk Removal Pricing"
        sub="Our pricing is primarily based on how much space your items take up in our truck. Every standard load includes the labor, loading, hauling, transportation, and cleanup needed to get the job done."
      >
        <EstimateButton label="Get a Free Estimate" variant="primary" />
      </PageHero>

      {/* Pricing table: responsive table on desktop, stacked cards on mobile */}
      <section className="bg-cream py-20">
        <div className="container-x">
          <Reveal>
            <div className="mb-8 flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-gold-dark">
              <PawPrint className="h-4 w-4" strokeWidth={2.4} /> Starting Prices
            </div>
          </Reveal>

          {/* Desktop table */}
          <Reveal className="mx-auto hidden max-w-3xl overflow-hidden rounded-lg border border-border sm:block">
            <table className="w-full border-collapse text-left" data-testid="pricing-table">
              <thead>
                <tr className="bg-forest text-cream">
                  <th className="px-6 py-4 font-heading text-sm font-bold uppercase tracking-wide">Load Size</th>
                  <th className="px-6 py-4 font-heading text-sm font-bold uppercase tracking-wide">Approx. Volume</th>
                  <th className="px-6 py-4 text-right font-heading text-sm font-bold uppercase tracking-wide">Starting Price</th>
                </tr>
              </thead>
              <tbody>
                {pricing.map((p, i) => (
                  <tr key={p.size} className={`border-t border-border ${i % 2 ? "bg-accent/40" : "bg-white"}`}>
                    <td className="px-6 py-4 font-semibold text-forest">{p.size}</td>
                    <td className="px-6 py-4 text-charcoal/70">{p.volume}</td>
                    <td className="px-6 py-4 text-right font-heading text-lg font-extrabold text-gold-dark">${p.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>

          {/* Mobile stacked cards */}
          <StaggerGroup className="grid gap-3 sm:hidden" >
            {pricing.map((p) => (
              <StaggerItem key={p.size} className="flex items-center justify-between rounded-lg border border-border bg-white p-4">
                <div>
                  <p className="font-heading text-sm font-bold text-forest">{p.size}</p>
                  <p className="mt-0.5 text-xs text-mutedgreen">{p.volume}</p>
                </div>
                <p className="font-heading text-2xl font-extrabold text-gold-dark">${p.price}</p>
              </StaggerItem>
            ))}
          </StaggerGroup>

          <Reveal className="mx-auto mt-6 max-w-3xl text-center text-sm font-semibold text-mutedgreen">
            All prices shown are <span className="text-forest">starting prices</span>.
          </Reveal>
        </div>
      </section>

      <PawDivider className="bg-cream" />

      {/* Every price includes */}
      <section className="bg-accent/40 py-20">
        <div className="container-x">
          <Reveal>
            <SectionHeading eyebrow="Included in every load" title="Every Price Includes" align="center" sub="No confusing pricing. No wondering what basic service includes." />
          </Reveal>
          <StaggerGroup className="mx-auto mt-12 grid max-w-4xl gap-4 sm:grid-cols-2">
            {priceIncludes.map((p) => (
              <StaggerItem key={p.label} className="flex items-center gap-4 rounded-lg border border-border bg-white p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-gold/15 text-gold-dark">
                  <Icon name={p.icon} className="h-5 w-5" strokeWidth={2} />
                </span>
                <span className="font-semibold text-forest">{p.label}</span>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-cream py-20">
        <div className="container-x">
          <Reveal className="mx-auto max-w-3xl rounded-lg border border-border bg-white p-8">
            <div className="flex items-center gap-3">
              <Info className="h-5 w-5 text-gold-dark" />
              <h2 className="font-heading text-xl font-bold text-forest">A Note on Pricing</h2>
            </div>
            <div className="mt-4 space-y-3 text-sm leading-relaxed text-charcoal/75">
              <p>Prices are based primarily on volume. Final pricing may vary depending on the type, quantity, accessibility, weight, disposal requirements, and location of the items being removed.</p>
              <p>Heavy materials such as concrete, dirt, brick, roofing materials, and similar debris may be priced separately because of weight and disposal costs.</p>
              <p>Specialty items or items requiring additional disposal fees may also be priced separately.</p>
              <p className="font-semibold text-forest">Contact us for a free estimate before booking.</p>
            </div>
            <div className="mt-6">
              <EstimateButton label="Get a Free Estimate" />
            </div>
          </Reveal>
        </div>
      </section>

      <EstimateCTASection />
    </>
  );
}
